import { useCallback, useEffect, useRef } from 'react'

interface UseDisableMainScroll {
  disableMainScroll: () => void
  enableMainScroll: () => void
}

const isElementScrollable = (el: HTMLElement): boolean => {
  const style = window.getComputedStyle(el)
  return (
    el.scrollHeight > el.clientHeight &&
    (style.overflowY === 'scroll' || style.overflowY === 'auto')
  )
}

const findScrollableParent = (el: HTMLElement | null): HTMLElement | null => {
  if (!el || el === document.body || el === document.documentElement) {
    return null
  }
  if (isElementScrollable(el)) {
    return el
  }
  return findScrollableParent(el.parentElement)
}

export const useDisableMainScroll = (): UseDisableMainScroll => {
  const touchState = useRef<{ startY: number; isTargetScrollable: boolean }>({
    startY: 0,
    isTargetScrollable: false,
  }).current

  const listenersActive = useRef(false)

  const preventRefresh = (e: TouchEvent) => {
    if (
      window.scrollY === 0 &&
      e.touches[0].clientY > touchState.startY &&
      !touchState.isTargetScrollable
    ) {
      e.preventDefault()
    }
  }

  const recordStartPosition = (e: TouchEvent) => {
    touchState.startY = e.touches[0].clientY
    touchState.isTargetScrollable = !!findScrollableParent(e.target as HTMLElement)
  }

  const disableMainScroll = useCallback(() => {
    if (!listenersActive.current) {
      window.addEventListener('touchstart', recordStartPosition, { passive: true })
      window.addEventListener('touchmove', preventRefresh, { passive: false })
      listenersActive.current = true
    }
  }, [])

  const enableMainScroll = useCallback(() => {
    if (listenersActive.current) {
      window.removeEventListener('touchstart', recordStartPosition)
      window.removeEventListener('touchmove', preventRefresh)
      listenersActive.current = false
    }
  }, [])

  useEffect(() => {
    return () => enableMainScroll()
  }, [])

  return { disableMainScroll, enableMainScroll }
}
